import Time from '@/utils/Time';
import API from '@/services/api';
import { connect } from 'react-redux';
import FONTS from '@/assets/fonts.json';
import { isIOS } from '@CS/utils/Device';
import { Images } from '@/assets/images';
// import { sentryDSN } from '@/utils/API';
import { User, MCD } from '@/services/app';
import { useState, useEffect } from 'react';
import { Login, Home, Game } from '@/routes';

import CloseButton from '@/components/CloseButton';
import { getLogged } from '@/redux/auth/selectors';
import type { UserState } from '@/redux/user/types';
import { isDEV, isStaging } from '@/utils/Location';
// import { RequestError } from '@CS/components/Popups';
import { getUserState } from '@/redux/user/selectors';
import { useRecaptcha, useNavigateQuery } from '@/hooks';
// import type { RequestErrorInfo } from '@/services/api/types';
import { Routes, Route, useLocation } from 'react-router-dom';
// import { withSentryReactRouterV6Routing } from '@sentry/react';
import TranslationProvider from '@CS/components/TranslationProvider';
import { useFonts, useImages, useLoader, useNetworkListener } from '@CS/hooks';

type ApplicationProps = {
  logged?: boolean;
  login?: boolean;
  user: UserState;
};

const Application = ({ user, logged, login = true }: ApplicationProps) => {
  const { search } = useLocation();

  const imagesLoaded = useImages({
    ...Images
  });

  const fontsLoaded = useFonts(FONTS);
  const navigate = useNavigateQuery();

  const network = useNetworkListener();
  const { recaptcha } = useRecaptcha();

  const homeRedirect = () => setLoading(false);
  const [loading, setLoading] = useState(true);
  const [minLoadingTime, setMinLoadingTime] = useState(true);

  const { email, points, country } = user ?? {};
  // const cancelLogin = () => setLoginError(void 0);

  useEffect(() => {
    const timer = setTimeout(() => setMinLoadingTime(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const loader = useLoader(
    loading || !recaptcha || !fontsLoaded || !imagesLoaded || minLoadingTime,
    {
      color: 'white'
    }
  );

  const onCloseClick = () =>
    isIOS
      ? MCD.openHome()
      : window.location.replace(
          isStaging
            ? 'gmalite://gmalite-smartweb?weburl=https%3A%2F%2Ffamily-homepage.staging.mcd-sa.platform-tbwa-x.com%2F'
            : 'gmalite://gmalite-smartweb?weburl=https%3A%2F%2Ffamily-homepage.production.mcd-sa.platform-tbwa-x.com%2F'
        );

  // const [loginError, setLoginError] = useState<RequestErrorInfo>();

  const toggleFullscreen = () =>
    !isDEV() && ~MCD.toggleFullscreen() && MCD.orientationScreen();

  const onVisibilityChange = () =>
    document.visibilityState === 'visible' && toggleFullscreen();

  const getUserData = () =>
    Promise.all([User.checkUserPoints(), User.checkUserData()]);

  const handleAuth = () =>
    (!email || typeof points !== 'number') &&
    getUserData().then(homeRedirect).catch(loginRedirect);

  const initApplication = () => {
    API.revokeToken();
    toggleFullscreen();
    User.checkUserCountry();
    User.checkUserLanguage();
    login ? handleAuth() : homeRedirect();
  };

  const loginRedirect = () => {
    setLoading(false);
    navigate('/login');
  };

  const onLogin = async () => {
    toggleFullscreen();
    await getUserData();
    setLoading(false);
  };

  useEffect(() => {
    const userInfo = country && email;
    const ready = recaptcha && !logged;

    ready && userInfo && API.login(user);
    // .catch(error =>
    //   setLoginError(API.getRequestErrorInfo(error))
    // );
  }, [recaptcha, logged, country, email, user]);

  useEffect(() => {
    MCD.App.mcd && !loading && initApplication();
  }, [MCD.App.mcd]);

  useEffect(() => {
    Time.setCurrentTime(search);
  }, [search]);

  useEffect(() => {
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('mcdBridgeReady', initApplication);
    (MCD.App.mcd || isDEV()) && initApplication();

    return () => {
      document.removeEventListener('mcdBridgeReady', initApplication);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  /**
   * Sentry Tracing Routes Wrapper:
   *
  const RoutesWrapper = sentryDSN
    ? withSentryReactRouterV6Routing(Routes)
    : Routes; */

  return (
    <>
      <CloseButton onClick={onCloseClick} />
      {loader || network || (
        <TranslationProvider lang={user.language}>
          <Routes>
            {/* <RoutesWrapper> */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login onLogin={onLogin} />} />
            <Route path="/game/:index" element={<Game />} />
            {/* </RoutesWrapper> */}
          </Routes>

          {/* {loginError && <RequestError info={loginError} onClose={cancelLogin} />} */}
        </TranslationProvider>
      )}
    </>
  );
};

const mapStateToProps = (state: never) => ({
  user: getUserState(state),
  logged: getLogged(state)
});

export default connect(mapStateToProps)(Application);
